import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Divider, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { fetchTokenMetadata } from './tokenIntelligenceApi';
import { deriveCounterpartyInsights } from './counterpartyIntelligence';

const useStyles = makeStyles((theme) => ({
  panel: {
    background: 'rgba(10, 14, 30, 0.88)',
    border: '1px solid rgba(77, 121, 255, 0.22)',
    borderRadius: 14,
    padding: 14,
    textAlign: 'left',
    boxSizing: 'border-box',
    [theme.breakpoints.down('sm')]: {
      padding: 12,
    },
  },
  title: {
    fontSize: 12,
    fontWeight: 900,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    color: '#9ea4d8',
    marginBottom: 10,
  },
  divider: {
    background: 'rgba(255,255,255,0.08)',
    marginBottom: 12,
  },
  intro: {
    color: '#c9cff8',
    fontSize: 13,
    lineHeight: 1.55,
    marginBottom: 12,
  },
  summaryRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    marginBottom: 12,
  },
  chip: {
    borderRadius: 999,
    border: '1px solid rgba(77, 121, 255, 0.28)',
    background: 'rgba(77, 121, 255, 0.12)',
    color: '#cfd6ff',
    fontSize: 11,
    fontWeight: 700,
    padding: '4px 10px',
  },
  row: {
    borderRadius: 12,
    border: '1px solid rgba(77, 121, 255, 0.18)',
    background: 'rgba(12, 18, 38, 0.85)',
    padding: '10px 12px',
    marginBottom: 8,
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'rgba(77, 121, 255, 0.42)',
      background: 'rgba(16, 24, 50, 0.92)',
    },
  },
  rowTitle: {
    color: '#e5e7ff',
    fontSize: 13,
    fontWeight: 800,
    lineHeight: 1.4,
  },
  rowMeta: {
    color: '#8d93c9',
    fontSize: 11,
    lineHeight: 1.35,
    marginTop: 4,
    wordBreak: 'break-word',
  },
  tagsRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 8,
  },
  tag: {
    borderRadius: 999,
    border: '1px solid rgba(77, 121, 255, 0.28)',
    background: 'rgba(77, 121, 255, 0.12)',
    color: '#cfd6ff',
    fontSize: 10,
    fontWeight: 700,
    padding: '3px 8px',
  },
  empty: {
    color: '#8d93c9',
    fontSize: 12,
    lineHeight: 1.45,
  },
  footer: {
    color: '#9ea4d8',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 10,
  },
  error: {
    color: '#ffb4b4',
    fontSize: 12,
    lineHeight: 1.45,
    marginTop: 8,
  },
  actionButton: {
    textTransform: 'none',
    fontWeight: 700,
    borderRadius: 10,
    color: '#d9defa',
    borderColor: 'rgba(255,255,255,0.18)',
    marginTop: 8,
  },
}));

function normalizeAddress(value) {
  const raw = String(value || '').trim().toLowerCase();
  return /^0x[0-9a-f]{40}$/.test(raw) ? raw : '';
}

function shortenHex(value) {
  const raw = String(value || '').trim();
  if (!/^0x[0-9a-fA-F]{40}$/.test(raw)) return raw || 'Unknown';
  return `${raw.slice(0, 6)}...${raw.slice(-4)}`;
}

function extractAddressMatches(input) {
  if (!input) return [];
  if (typeof input === 'string') return input.match(/0x[0-9a-fA-F]{40}/g) || [];
  if (Array.isArray(input)) return input.flatMap((value) => extractAddressMatches(value));
  if (typeof input === 'object') return Object.values(input).flatMap((value) => extractAddressMatches(value));
  return [];
}

function recencyBoost(value) {
  const raw = String(value || '').trim();
  if (!raw) return 0;
  const ts = new Date(raw).getTime();
  if (!Number.isFinite(ts) || ts <= 0) return 0;
  const ageHours = Math.max(0, (Date.now() - ts) / (1000 * 60 * 60));
  if (ageHours <= 24) return 1.5;
  if (ageHours <= 24 * 7) return 0.75;
  return 0;
 }

function deriveConnectionTier(score) {
  const n = Number(score);
  if (!Number.isFinite(n) || n <= 0) return 'Emerging connection';
  if (n >= 8) return 'High-conviction neighbor';
  if (n >= 5) return 'Strong linked token';
  if (n >= 3) return 'Moderate related token';
  return 'Emerging connection';
}

function buildSummaryChips(candidates, liquidity) {
  const out = [];
  const quoteToken = normalizeAddress(liquidity && liquidity.pool_stats && liquidity.pool_stats.quote_token);
  out.push(`Ranked Tokens ${Array.isArray(candidates) ? candidates.length : 0}`);
  if (quoteToken) out.push(`Quote Token ${shortenHex(quoteToken)}`);
  if (Array.isArray(candidates) && candidates[0]) out.push(deriveConnectionTier(candidates[0].score));
  return out.slice(0, 3);
}

function buildTokenContext(address, metadata) {
  const tokenAddress = String(address || '').trim();
  const tokenLabel = metadata && (metadata.symbol || metadata.name)
    ? `${metadata.symbol || metadata.name}${metadata && metadata.name && metadata.symbol ? ` • ${metadata.name}` : ''}`
    : tokenAddress;
  const params = new URLSearchParams();
  if (tokenAddress) params.set('tokenAddress', tokenAddress);
  return {
    search: params.toString() ? `?${params.toString()}` : '',
    state: {
      tokenAddress,
      tokenLabel,
      tokenSymbol: metadata && metadata.symbol ? metadata.symbol : '',
      from: `/token/${tokenAddress}`,
    },
  };
 }

function buildRelatedCandidates(address, metadata, liquidity, stories, theses) {
  const current = normalizeAddress(address);
  const byAddress = new Map();
  const currentCategories = Array.isArray(metadata && metadata.categories) ? metadata.categories.map((item) => String(item).toLowerCase()) : [];
  const storyRows = Array.isArray(stories && stories.stories) ? stories.stories : [];
  const thesisRows = Array.isArray(theses && theses.theses) ? theses.theses : [];

  const upsert = (candidate, reason, weight = 1) => {
    const addr = normalizeAddress(candidate);
    if (!addr || addr === current) return;
    const existing = byAddress.get(addr) || { address: addr, score: 0, reasons: new Set(), directMentions: 0 };
    existing.score += weight;
    if (reason) existing.reasons.add(reason);
    if (weight >= 3) existing.directMentions += 1;
    byAddress.set(addr, existing);
  };

  const quoteToken = normalizeAddress(liquidity && liquidity.pool_stats && liquidity.pool_stats.quote_token);
  if (quoteToken && quoteToken !== current) {
    upsert(quoteToken, 'Quote Token Relationship', 2.5);
  }

  storyRows.forEach((story) => {
    const metaObj = story && story.meta && typeof story.meta === 'object' ? story.meta : {};
    const direct = [story && story.primary_token, metaObj.token, metaObj.token_address, metaObj.contract];
    const boost = recencyBoost(story && (story.ts_created || story.ts_updated));
    direct.forEach((candidate) => upsert(candidate, 'Story Co-Mention', 3 + boost));
    extractAddressMatches(metaObj).forEach((candidate) => upsert(candidate, 'Shared Narrative', 1 + boost));
    extractAddressMatches(story && story.subtitle).forEach((candidate) => upsert(candidate, 'Narrative Text', 0.75 + boost));
  });

  thesisRows.forEach((thesis) => {
    const boost = recencyBoost(thesis && (thesis.ts_updated || thesis.ts_created));
    extractAddressMatches(thesis && thesis.thesis_text).forEach((candidate) => upsert(candidate, 'Thesis Evidence', 2 + boost));
    extractAddressMatches(thesis && thesis.invalidation_signal).forEach((candidate) => upsert(candidate, 'Thesis Evidence', 1 + boost));
    extractAddressMatches(thesis && thesis.entry_signal).forEach((candidate) => upsert(candidate, 'Thesis Evidence', 1 + boost));
    const thesisCategories = Array.isArray(thesis && thesis.story_types) ? thesis.story_types : [];
    if (currentCategories.length && thesisCategories.some((item) => currentCategories.includes(String(item).toLowerCase()))) {
      const entity = normalizeAddress(thesis && thesis.entity_address);
      if (entity) upsert(entity, 'Category Match', 1);
    }
  });

  const counterparties = deriveCounterpartyInsights({ address, liquidity, stories, theses });
  counterparties.tokens.forEach((row) => {
    upsert(row.address, 'Counterparty Overlap', Math.min(3, Number(row.score || 0) * 0.45));
    (Array.isArray(row.reasons) ? row.reasons : []).forEach((reason) => {
      if (reason === 'Pool quote token') {
        upsert(row.address, 'Quote Token Relationship', 0.75);
      }
    });
  });

  return [...byAddress.values()]
    .sort((a, b) => b.score - a.score || b.directMentions - a.directMentions || a.address.localeCompare(b.address))
    .slice(0, 5)
    .map((row) => ({ ...row, reasons: [...row.reasons].slice(0, 3), score: Number(row.score.toFixed(2)) }));
}

export default function RelatedTokensPanel({ address, metadata, liquidity, stories, theses, enrichMetadata = true }) {
  const classes = useStyles();
  const navigate = useNavigate();
  const [detailsByAddress, setDetailsByAddress] = useState({});
  const [detailsError, setDetailsError] = useState('');
  const candidates = useMemo(() => buildRelatedCandidates(address, metadata, liquidity, stories, theses), [address, metadata, liquidity, stories, theses]);
  const tokenContext = useMemo(() => buildTokenContext(address, metadata), [address, metadata]);
  const summaryChips = useMemo(() => buildSummaryChips(candidates, liquidity), [candidates, liquidity]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (!enrichMetadata) {
        setDetailsByAddress({});
        setDetailsError('');
        return;
      }
      if (!candidates.length) {
        setDetailsByAddress({});
        setDetailsError('');
        return;
      }
      try {
        const results = await Promise.allSettled(candidates.slice(0, 4).map((row) => fetchTokenMetadata(row.address)));
        if (cancelled) return;
        const next = {};
        results.forEach((result, index) => {
          if (result.status === 'fulfilled' && result.value) {
            next[candidates[index].address] = result.value;
          }
        });
        setDetailsByAddress(next);
        setDetailsError('');
      } catch (e) {
        if (!cancelled) setDetailsError('Failed to enrich related token metadata.');
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [candidates, enrichMetadata]);

  return (
    <Box className={classes.panel}>
      <Typography className={classes.title}>Related Tokens</Typography>
      <Divider className={classes.divider} />
      <Typography className={classes.intro}>
        Discover nearby tokens connected through stories, theses, and pool context so you can branch into the strongest narrative neighbors first.
      </Typography>

      {summaryChips.length ? (
        <Box className={classes.summaryRow}>
          {summaryChips.map((item) => (
            <Box key={item} className={classes.chip}>{item}</Box>
          ))}
        </Box>
      ) : null}

      {candidates.length ? candidates.map((candidate) => {
        const details = detailsByAddress[candidate.address] || null;
        const displayName = details && (details.symbol || details.name)
          ? `${details.symbol || details.name}${details && details.name && details.symbol ? ` • ${details.name}` : ''}`
          : shortenHex(candidate.address);
        return (
          <Box key={candidate.address} className={classes.row} onClick={() => navigate(`/token/${candidate.address}`)}>
            <Typography className={classes.rowTitle}>{displayName}</Typography>
            <Typography className={classes.rowMeta}>
              {shortenHex(candidate.address)} • {deriveConnectionTier(candidate.score)} • Score {candidate.score}
            </Typography>
            <Box className={classes.tagsRow}>
              {candidate.reasons.map((reason) => (
                <Box key={reason} className={classes.tag}>{reason}</Box>
              ))}
            </Box>
          </Box>
        );
      }) : <Typography className={classes.empty}>No related tokens derived from recent narrative inputs.</Typography>}

      <Button className={classes.actionButton} variant="outlined" onClick={() => navigate(`/stories${tokenContext.search}`, { state: tokenContext.state })}>Open Neighbor Evidence</Button>
      <Typography className={classes.footer}>
        Related tokens are derived from co-mentioned token addresses, thesis evidence, and quote-token / counterparty overlap, then ranked by direct mentions, recency, and supporting narrative strength.
      </Typography>
      {detailsError ? <Typography className={classes.error}>{detailsError}</Typography> : null}
    </Box>
  );
}
